import { Box, Typography, ToggleButtonGroup, ToggleButton, Paper, Divider } from "@mui/material";
import { YearlyProfit } from "./charts/YearlyProfit";
import { YearlyOverview } from "./charts/YearlyOverview";
import { MonthlyOverview } from "./charts/MonthlyOverview";
import { CategorySunBurst } from "./charts/CategorySunBurst";
import { useState } from "react";
import { AnalyticsOutlined, CalendarToday, DateRange, KeyboardArrowDown } from "@mui/icons-material";

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;
const MIN_YEAR = 2021;
const YEARS = Array.from({ length: CURRENT_YEAR - MIN_YEAR + 1 }, (_, i) => CURRENT_YEAR - i);
const MONTHS = [
    { value: 1, label: '1月' },
    { value: 2, label: '2月' },
    { value: 3, label: '3月' },
    { value: 4, label: '4月' },
    { value: 5, label: '5月' },
    { value: 6, label: '6月' },
    { value: 7, label: '7月' },
    { value: 8, label: '8月' },
    { value: 9, label: '9月' },
    { value: 10, label: '10月' },
    { value: 11, label: '11月' },
    { value: 12, label: '12月' }
];

// 年份下拉选择组件
const YearSelector = ({ value, onChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handlePrevYear = () => onChange(Math.max(MIN_YEAR, value - 1));
    const handleNextYear = () => onChange(Math.min(CURRENT_YEAR, value + 1));

    return (
        <Box sx={{ position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ToggleButton
                    onClick={handlePrevYear}
                    disabled={value <= MIN_YEAR}
                    sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        p: 1,
                        minWidth: 36,
                        color: '#6b7280',
                        '&.Mui-disabled': { borderColor: 'transparent', bgcolor: 'grey.100' },
                        '&:hover': { bgcolor: 'grey.50' }
                    }}
                >
                    <KeyboardArrowDown sx={{ transform: 'rotate(90deg)', fontSize: 18 }} />
                </ToggleButton>

                <ToggleButton
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        fontSize: 14,
                        fontWeight: 500,
                        textTransform: 'none',
                        color: '#374151',
                        bgcolor: 'white',
                        minWidth: 80,
                        '&:hover': { bgcolor: 'grey.50' }
                    }}
                >
                    {value}年 <KeyboardArrowDown sx={{ fontSize: 18, ml: 0.5 }} />
                </ToggleButton>

                <ToggleButton
                    onClick={handleNextYear}
                    disabled={value >= CURRENT_YEAR}
                    sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        p: 1,
                        minWidth: 36,
                        color: '#6b7280',
                        '&.Mui-disabled': { borderColor: 'transparent', bgcolor: 'grey.100' },
                        '&:hover': { bgcolor: 'grey.50' }
                    }}
                >
                    <KeyboardArrowDown sx={{ transform: 'rotate(-90deg)', fontSize: 18 }} />
                </ToggleButton>
            </Box>

            {open && (
                <Paper
                    elevation={3}
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 36,
                        mt: 1,
                        zIndex: 1000,
                        maxHeight: 300,
                        overflowY: 'auto',
                        minWidth: 100
                    }}
                >
                    {YEARS.map((y) => (
                        <ToggleButton
                            key={y}
                            value={y}
                            selected={value === y}
                            onClick={() => {
                                onChange(y);
                                setAnchorEl(null);
                            }}
                            fullWidth
                            sx={{
                                border: 'none',
                                borderRadius: 0,
                                py: 1,
                                px: 2,
                                fontSize: 14,
                                fontWeight: 500,
                                textTransform: 'none',
                                color: '#6b7280',
                                justifyContent: 'flex-start',
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'white'
                                },
                                '&:hover': {
                                    bgcolor: value === y ? 'primary.dark' : 'grey.100'
                                }
                            }}
                        >
                            {y}年
                        </ToggleButton>
                    ))}
                </Paper>
            )}
            {open && <Box sx={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setAnchorEl(null)} />}
        </Box>
    );
};

// 月份下拉选择组件
const MonthSelector = ({ value, onChange, year }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handlePrevMonth = () => {
        if (value > 1) {
            onChange(value - 1);
        }
    };

    const handleNextMonth = () => {
        const maxMonth = year === CURRENT_YEAR ? CURRENT_MONTH : 12;
        if (value < maxMonth) {
            onChange(value + 1);
        }
    };

    const maxMonth = year === CURRENT_YEAR ? CURRENT_MONTH : 12;

    return (
        <Box sx={{ position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ToggleButton
                    onClick={handlePrevMonth}
                    disabled={value <= 1}
                    sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        p: 1,
                        minWidth: 36,
                        color: '#6b7280',
                        '&.Mui-disabled': { borderColor: 'transparent', bgcolor: 'grey.100' },
                        '&:hover': { bgcolor: 'grey.50' }
                    }}
                >
                    <KeyboardArrowDown sx={{ transform: 'rotate(90deg)', fontSize: 18 }} />
                </ToggleButton>

                <ToggleButton
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        fontSize: 14,
                        fontWeight: 500,
                        textTransform: 'none',
                        color: '#374151',
                        bgcolor: 'white',
                        minWidth: 80,
                        '&:hover': { bgcolor: 'grey.50' }
                    }}
                >
                    {MONTHS.find(m => m.value === value)?.label} <KeyboardArrowDown sx={{ fontSize: 18, ml: 0.5 }} />
                </ToggleButton>

                <ToggleButton
                    onClick={handleNextMonth}
                    disabled={value >= maxMonth}
                    sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        p: 1,
                        minWidth: 36,
                        color: '#6b7280',
                        '&.Mui-disabled': { borderColor: 'transparent', bgcolor: 'grey.100' },
                        '&:hover': { bgcolor: 'grey.50' }
                    }}
                >
                    <KeyboardArrowDown sx={{ transform: 'rotate(-90deg)', fontSize: 18 }} />
                </ToggleButton>
            </Box>

            {open && (
                <Paper
                    elevation={3}
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 36,
                        mt: 1,
                        zIndex: 1000,
                        p: 1
                    }}
                >
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.5 }}>
                        {MONTHS.slice(0, maxMonth).map((m) => (
                            <ToggleButton
                                key={m.value}
                                value={m.value}
                                selected={value === m.value}
                                onClick={() => {
                                    onChange(m.value);
                                    setAnchorEl(null);
                                }}
                                sx={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 1.5,
                                    py: 0.75,
                                    fontSize: 13,
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    color: '#6b7280',
                                    '&.Mui-selected': {
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        borderColor: 'primary.main'
                                    },
                                    '&:hover': {
                                        bgcolor: value === m.value ? 'primary.dark' : 'grey.50'
                                    }
                                }}
                            >
                                {m.label}
                            </ToggleButton>
                        ))}
                    </Box>
                </Paper>
            )}
            {open && <Box sx={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setAnchorEl(null)} />}
        </Box>
    );
};

export const Statistics = () => {
    const [viewType, setViewType] = useState('yearly'); // yearly, monthly
    const [year, setYear] = useState(CURRENT_YEAR);
    const [month, setMonth] = useState(CURRENT_MONTH);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f9fafb' }}>
            {/* 顶部选择栏 */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 0,
                    bgcolor: 'white',
                    borderBottom: '1px solid #e5e7eb',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                }}
            >
                <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        {/* 标题 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                p: 1,
                                borderRadius: 2,
                                bgcolor: 'primary.main',
                                color: 'white',
                                display: 'flex'
                            }}>
                                <AnalyticsOutlined sx={{ fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>
                                    统计面板
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {viewType === 'yearly' ? `${year}年度` : `${year}年${month}月`}
                                </Typography>
                            </Box>
                        </Box>

                        {/* 选择器组 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {/* 统计类型切换 */}
                            <ToggleButtonGroup
                                value={viewType}
                                exclusive
                                size="small"
                                onChange={(_, v) => v && setViewType(v)}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 2,
                                        py: 0.75,
                                        px: 1.5,
                                        fontSize: 13,
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        color: '#6b7280',
                                        '&.Mui-selected': {
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            borderColor: 'primary.main',
                                            '&:hover': { bgcolor: 'primary.dark' }
                                        },
                                        '&:hover': {
                                            bgcolor: 'grey.50'
                                        }
                                    }
                                }}
                            >
                                <ToggleButton value="yearly">
                                    <DateRange sx={{ fontSize: 16, mr: 0.5 }} />年度
                                </ToggleButton>
                                <ToggleButton value="monthly">
                                    <CalendarToday sx={{ fontSize: 16, mr: 0.5 }} />月度
                                </ToggleButton>
                            </ToggleButtonGroup>

                            <Divider orientation="vertical" flexItem />

                            {/* 年份选择 */}
                            <YearSelector value={year} onChange={setYear} />

                            {/* 月份选择 - 仅月度统计显示 */}
                            {viewType === 'monthly' && (
                                <MonthSelector value={month} onChange={setMonth} year={year} />
                            )}
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* 内容区 */}
            <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 3 }}>
                {/* 年度统计 */}
                {viewType === 'yearly' && (
                    <>
                        <YearlyOverview year={year} />
                        <Box sx={{ mb: 3 }}>
                            <YearlyProfit year={year} />
                        </Box>
                        <Box>
                            <CategorySunBurst year={year} dataUrl="/api/statistic/yearly_category/" />
                        </Box>
                    </>
                )}

                {/* 月度统计 */}
                {viewType === 'monthly' && (
                    <>
                        <MonthlyOverview year={year} month={month} />
                        <Box>
                            <CategorySunBurst year={year} month={month} dataUrl="/api/statistic/monthly_category_tree/" />
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};
